import React, {
  useEffect, useState, useRef,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import NatConfigurationForm from 'routes/NatConfiguration/containers/NatConfigurationCreate/components/NatConfigurationForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/natConfiguration/natConfiguration.selectors';
import * as actions from 'data/natConfiguration/natConfiguration.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as types from 'data/natConfiguration/natConfiguration.types';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { NatConfiguration } from 'data/natConfiguration/natConfiguration.types';
import natConfigurationTemplate from '../../templates/index';
import { adaptTemplateVisibilityMode } from '../../utils/NatConfiguration.adapter';
import '../../styles.scss'

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => types.NatConfiguration
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  natConfigurationTemplateDef: adaptTemplateVisibilityMode(natConfigurationTemplate, 'view'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
  selectedNatConfiguration: selectors.getSelectedNatConfiguration(state)[0],
  natConfiguration: selectors.getNatConfiguration(state),
  originalSelectedNatConfiguration: selectors.getSelectedNatConfiguration(state)[0],
});

const mapDispatch = {
  goToNatConfigurationEdit: actions.goToNatConfigurationEdit,
  goToNatConfigurationList: actions.goToNatConfigurationList,
  getNatConfiguration: actions.getNatConfiguration,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const NatConfigurationView = ({
  history,
  selectedNatConfiguration,
  natConfiguration,
  deliveryFunctionId,
  natConfigurationTemplateDef,
  goToNatConfigurationList,
  getNatConfiguration,
  goToNatConfigurationEdit
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<NatConfiguration>>({});
  const [template, setTemplate] = useState(natConfigurationTemplateDef);
  const { t } = useTranslation();

  const id = selectedNatConfiguration?.id;

  useEffect(() => {
    if (id) {
      getNatConfiguration(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getNatConfiguration]);

  useEffect(() => {
    if (natConfiguration != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      setFormData(natConfiguration);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.key = Date.now().toString();
      setTemplate(clonedTemplate);
    }
  }, [
    id,
    natConfiguration,
  ]);

  const handleCancel = () => goToNatConfigurationList(history);

  const handleEdit = () => goToNatConfigurationEdit(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as NatConfiguration);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = (section: string) => {
    if (formData && template) {
      return (
        <NatConfigurationForm
          section={section}
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <FormLayout>
      <FormLayoutContent>
        {renderFormPanel(t('natConfiguration.view.title'))}
        <h2 className="natConfigurationH2">{t('natConfiguration.section.private')}</h2>
        {renderForm('private')}
        <h2 className="natConfigurationH2">{t('natConfiguration.section.public')}</h2>
        {renderForm('public')}
      </FormLayoutContent>
    </FormLayout>
  );
};

export default withRouter(connector(NatConfigurationView));
